import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@mui/styles';
import { useNavigate } from 'react-router-dom';

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import AddIcon from '@mui/icons-material/Add';
import Chip from '@mui/material/Chip';

import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import EqualizerIcon from '@mui/icons-material/Equalizer';

import Moment from 'moment';

import Auth from '../../../../data/auth-service.js';
import DashboardService from '../../../../data/dashboard-service';
import DataContext from '../../../../data/data-context.js';

import Validator, { useValidate, checkValidity } from '../../../../components/validator';
import PopupMenu from '../../../../components/popupmenu';

const useStyles = makeStyles(theme => ({
    root: {
    },
    label: {
        textTransform: 'capitalize',
        display: 'inline',
    },
    secondary: {
        backgroundColor: theme.palette.secondary.main,
    },
    primary: {
        backgroundColor: theme.palette.primary.main,
    },
    // editor classes
    editorRoot: {
        padding: theme.spacing(1),
    },
    editorFooter: {
        alignSelf: 'flex-end',
        width: '100%',
    },
    editorMargin: {
        margin: theme.spacing(2),
    },
    inlineChips: {
        display: 'inline-block',
        verticalAlign: 'middle',
        padding: 5,
    },
    inlineForm: {
        margin: theme.spacing(1),
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
        },
    },
}));

const DEFAULTS = edit => edit
    ? { minW: 12, minH: 10 }
    : { minW: 4, minH: 7 };

// don't display until size-me can figure out what our size is
const HealthAlertsWidget = (props) => {
    return !props.size || !props.size.width ? ''
        : (
            props.data.edit
                ? <Editor { ...props }/>
                : <Widget { ...props } />
        );
};
HealthAlertsWidget.propTypes = {
    data: PropTypes.object.isRequired,
};

const Widget = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const [, setErrors] = useState(0);
    const [data, setData] = useState([]);

    const sensorPerms = Auth.permissions.get('Sensors');

    const fetch = controller => {
        props.setBusy(true);

        if (sensorPerms.read) {
            return DashboardService.getHealthAlertsData({
                type: props.data.settings.type,
                uuids: (props.data.settings.targets || []).map(t => t.uuid),
            }, controller).catch(err => {
                props.onError(err, props.data.title);
                setErrors(current => {
                    props.setBusy(false, current > 2);
                    if (current > 2) DashboardService.unsubscribe(props.data.id);
                    return current + 1;
                });
                return 'error';
            });
        }
    };

    const callback = res => {
        if (res === 'error') return;
        setErrors(0);
        props.setBusy(false);

        setData(res);
    };

    const subscribe = () => {
        setErrors(0);
        DashboardService.subscribe(props.data.id, fetch, callback);
    };

    const label = s => (s || '').split('_').join(' ');

    const handleClickAlert = d => {
        if (!d) return;
        const page = d.type.toLowerCase() === 'sensor' ? `sensors/health/${d.uuid}` : 'system/cmc_health';
        navigate(`/${page}`);
    };

    // some of those dates coming back from the sensor are not in ISO format
    const sanitize = d => (d || '')
        .split('-')
        .map(s => s.length < 2 ? '0' + s : s)
        .join('-');

    // incoming
    useEffect(() => {
        subscribe();
        return () => {
            // unsubscribe to data feed while editing (widget control exit)
            // will also force a refresh when resubscribing after edit is done
            DashboardService.unsubscribe(props.data.id);
        };
    }, []);

    return (
        <div
            className={classes.root}
            style={{ width: props.size.width, height: props.size.height, overflow: 'auto' }}>
        { data.length === 0 ? (
            <Typography variant="body2" style={{ padding: 16, textAlign: 'center' }}>
                No health alerts found
            </Typography>
        ) : ( data.map((d, i) => (
                <List key={`${d.uuid}_${i}`}>
                    <ListItem alignItems="flex-start">
                        <ListItemAvatar style={{ cursor: 'pointer' }} onClick={() => handleClickAlert(d)}>
                            <Avatar className={d.type !== 'Sensor' ? classes.primary : classes.secondary}>
                                { d.type === 'Sensor' ? <EqualizerIcon/> : <SettingsApplicationsIcon/>}
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                            primary={
                                <React.Fragment>
                                    {d.name} &nbsp;
                                    <Typography
                                        component="span"
                                        variant="subtitle2"
                                        className={classes.label}
                                        color="secondary">
                                        {Moment(sanitize(d.timestamp)).format(theme.date.long)}
                                    </Typography>
                                </React.Fragment>
                            }
                            secondary={
                                <React.Fragment>
                                    <Typography
                                        component="span"
                                        variant="body2"
                                        className={classes.label}
                                        color="textPrimary">
                                        {label(d.id)}: &nbsp;
                                    </Typography>
                                    {d.message}
                                </React.Fragment>
                            }
                        />
                    </ListItem>
                    {i < data.length - 1 && <Divider variant="inset" component="li" />}
                </List>
                    )))}
        </div>
    );
};

Widget.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    hover: PropTypes.bool,
    setBusy: PropTypes.func,
    onError: PropTypes.func,
};

const Editor = (props) => {
    const classes = useStyles();
    const daForm = useValidate({});

    const { sensorNames } = useContext(DataContext);
    const [title, setTitle] = useState(props.data.title);
    const [type, setType] = useState(props.data.settings.type || 'All');
    const [targets, setTargets] = useState(props.data.settings.targets || []);
    const [sensors, setSensors] = useState([]);

    const handleTitleChange = (e, validID, valid) => {
        daForm.set({ ...daForm.get(), [validID]: valid });
        setTitle(e.target.value);
    };

    const handleTypeChange = e => setType(e.target.value);

    const handleRemoveTarget = t => {
        const i = targets.findIndex(tt => t.uuid === tt.uuid);
        if (i >= 0) targets.splice(i, 1);
        setTargets([...targets]);
    };

    const handleAddTarget = t => {
        const i = targets.findIndex(tt => t.uuid === tt.uuid);
        if (i < 0) targets.push(t);
        setTargets([...targets]);
    };

    const handleSaveChanges = () => {
        props.onOK({
            ...props.data,
            title,
            settings: {
                ...props.data.settings,
                type,
                targets,
            },
        });
    };

    const isValid = () => {
        const formState = daForm.get() || {};
        if (Object.keys(formState).length === 0 && title && title.trim().length > 0) {
            return true;
        }
        return checkValidity(formState);
    };

    // initial hook
    useEffect(() => {
        setSensors(Object.entries(sensorNames).map(([k, v]) => ({ name: v.name, uuid: k })));
    }, [sensorNames]);

    return (
        <Box
            display="flex" flexWrap="wrap"
            className={classes.editorRoot}
            style={{ width: props.size.width - 5, height: props.size.height - 20 }}>
            <Box>
                <div className={classes.inlineForm}>
                    <Validator
                        style={{ width: 200 }}
                        label="Title"
                        aria-label="Title"
                        value={title}
                        onChange={handleTitleChange}/>
                    <TextField
                        style={{ width: 100 }}
                        select
                        label="Alert Source"
                        aria-label="Alert Source"
                        value={type}
                        onChange={handleTypeChange}>
                        {['All', 'CMC', 'Sensor'].map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                {type === 'Sensor' &&
                    <div className={classes.editorMargin}>
                        <Grid container spacing={1} alignItems="flex-end">
                            <Grid item>
                                <PopupMenu
                                    onSelect={handleAddTarget}
                                    aria-label="Filter by sensor(s)"
                                    title="Filter by sensor(s)"
                                    items={sensors}
                                    itemkey="uuid"
                                    itemTitle="name"
                                    button={
                                        <Button
                                            aria-label="filter"
                                            title="filter"
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            startIcon={<AddIcon/>}>Sensor
                                        </Button>
                                    }/>
                                {targets.map((t, i) =>
                                    <div key={`${t.name}_i`} className={classes.inlineChips}>
                                        <Chip
                                            color="default"
                                            label={t.name}
                                            onDelete={() => handleRemoveTarget(t)}
                                            className={classes.chip}/>
                                    </div>
                                )}
                            </Grid>
                        </Grid>
                    </div>
                }
            </Box>
            <Box className={classes.editorFooter}>
                <DialogActions style={{ paddingBottom: 0 }}>
                    <Button
                        aria-label="ok"
                        onClick={handleSaveChanges}
                        color="primary"
                        disabled={!isValid()}>
                    OK
                    </Button>
                    <Button aria-label="cancel" onClick={props.onCancel} color="secondary" autoFocus>
                    Cancel
                    </Button>
                </DialogActions>
            </Box>
        </Box>
    );
};
Editor.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    onOK: PropTypes.func,
    onCancel: PropTypes.func,
};

export {
    HealthAlertsWidget as default,
    DEFAULTS,
};
